/**
 * Acquisition Page - Sales and lead source analytics
 *
 * Shows:
 * - Cash metrics (full amounts, not abbreviated)
 * - Call statistics (Calls Due, Taken, Closed, Rates)
 * - Lead source breakdown chart
 * - Deal status breakdown chart
 * - Country breakdown chart
 * - Recent Calls table with CRUD
 *
 * Refactored to use DashboardShell widget system.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { AdditionalStatCardSkeleton, FilterBar, MetricCardSkeleton, StatCardSkeleton } from "../components";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useOfferDashboard } from "../context/OfferDashboardContext";
import { useSession } from "../auth/client";
import { DashboardDataProvider } from "../context/DashboardContext";
import { DashboardShell } from "../components/dashboard/DashboardShell";
import { ACQUISITION_DEFAULT } from "../components/dashboard/defaults";
import type { DashboardData } from "../components/dashboard/types";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { EditRowDialog, type FieldDefinition } from "../components/EditRowDialog";

interface DashboardMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  newCashCollected: number;
  mrrCash: number;
  totalGrossRevenue: number;
}

interface ChartDataItem {
  name: string;
  value: number;
  percentage?: number;
}

interface OutcomeItem {
  _id: string;
  count: number;
  cash: number;
}

interface FilterOption {
  value: string;
  label: string;
}

interface RecentCall {
  id: string;
  _id: string;
  timestamp: string;
  dateCallWasTaken: string;
  leadName: string;
  email: string;
  phone: string;
  closer: string;
  setter: string;
  status: "closed" | "no-show" | "pending" | "not-interested";
  callOutcome: string;
  cashCollected: number;
  notes: string;
}

// Field definitions for editing calls
const CALL_EDIT_FIELDS: FieldDefinition[] = [
  { key: "leadName", label: "Lead Name", type: "text", required: true },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "closer", label: "Closer", type: "text" },
  { key: "setter", label: "Setter", type: "text" },
  { key: "callOutcome", label: "Call Outcome", type: "text" },
  { key: "notes", label: "Notes", type: "textarea" },
];

export function Acquisition() {
  const { data: session } = useSession();
  const offerDashboard = useOfferDashboard();
  const { dateRange: globalDateRange, formatForApi } = useDateRange();
  const {
    dateField,
    closer,
    closers,
    setter,
    setters,
    source,
    sources,
    content,
    country,
    funnel,
    funnels,
    callOutcome,
    offer,
    setDateField,
    setClosers,
    setSetters,
    setSources,
    setContents,
    setCountries,
    setFunnels,
    setCallOutcome,
    offers,
    setOffers,
    clearAllFilters,
  } = useFilters();

  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<{ date: string; value: number }[]>([]);
  const [countryData, setCountryData] = useState<ChartDataItem[]>([]);
  const [leadSourceData, setLeadSourceData] = useState<ChartDataItem[]>([]);
  const [outcomeBreakdown, setOutcomeBreakdown] = useState<OutcomeItem[]>([]);
  const [filterOptions, setFilterOptions] = useState<{
    closers: string[];
    setters: string[];
    funnels: FilterOption[];
    contents: FilterOption[];
    countries: string[];
    sources: string[];
    offers: string[];
  }>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    sources: [],
    offers: [],
  });
  const [chartFilterMode, setChartFilterMode] = useState<"closed" | "all">("closed");

  // Recent Calls table state
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotalItems, setTableTotalItems] = useState(0);
  const [tableTotalPages, setTableTotalPages] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);

  // Edit/Delete state for recent calls
  const [editingCall, setEditingCall] = useState<RecentCall | null>(null);
  const [deletingCall, setDeletingCall] = useState<RecentCall | null>(null);

  // Track if the next fetch should be table-only (for pagination changes)
  const isPaginationChangeRef = useRef(false);

  // Handle edit
  const handleEditCall = (call: RecentCall) => {
    setEditingCall(call);
  };

  // Handle save
  const handleSaveCall = async (data: Record<string, unknown>) => {
    const mappedData = {
      prospectName: data.leadName,
      prospectEmail: data.email,
      prospectNumber: data.phone,
      closerName: data.closer,
      setterName: data.setter,
      callOutcome: data.callOutcome,
      callNotes: data.notes,
    };

    const dashboardQuery = offerDashboard.isEmbeddedMode && offerDashboard.dashboardSlug
      ? `?dashboard=${encodeURIComponent(offerDashboard.dashboardSlug)}`
      : "";
    const response = await fetch(`/api/leads/${data._id || data.id}${dashboardQuery}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mappedData),
    });

    if (!response.ok) {
      throw new Error("Failed to update call");
    }

    // Refresh data
    fetchData(false);
  };

  // Handle delete
  const handleDeleteCall = (call: RecentCall) => {
    setDeletingCall(call);
  };

  // Confirm delete
  const confirmDeleteCall = async () => {
    if (!deletingCall) return;

    const dashboardQuery = offerDashboard.isEmbeddedMode && offerDashboard.dashboardSlug
      ? `?dashboard=${encodeURIComponent(offerDashboard.dashboardSlug)}`
      : "";
    const response = await fetch(`/api/leads/${deletingCall._id || deletingCall.id}${dashboardQuery}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Failed to delete call");
    }

    // Refresh data
    fetchData(false);
  };

  // Handle pagination changes
  const handlePageChange = (page: number) => {
    isPaginationChangeRef.current = true;
    setTablePage(page);
  };

  const handlePageSizeChange = (size: number) => {
    isPaginationChangeRef.current = true;
    setTablePageSize(size);
    setTablePage(1);
  };

  // Fetch data
  // tableOnly: if true, only show table loading (for pagination changes)
  const fetchData = useCallback(async (showLoading = true, tableOnly = false) => {
    if (showLoading) {
      if (tableOnly) {
        setTableLoading(true);
      } else {
        setIsLoading(true);
      }
    }
    try {
      const params = new URLSearchParams();

      // Add date range
      const { startDate, endDate } = formatForApi();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);

      // Add filters
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (sources.length > 0) params.set("source", sources.join(","));
      if (content) params.set("content", content);
      if (country) params.set("country", country);
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (callOutcome) params.set("callOutcome", callOutcome);
      if (!offerDashboard.isEmbeddedMode) {
        if (offer && offer !== "all") params.set("offer", offer);
        const selectedOffers = offers.filter((o: string) => o !== "all");
        if (offer === "all" && selectedOffers.length > 0) params.set("offer", selectedOffers.join(","));
      }
      if (offerDashboard.isEmbeddedMode && offerDashboard.dashboardSlug) {
        params.set("dashboard", offerDashboard.dashboardSlug);
      }

      // Set closedOnly based on chart filter mode
      params.set("closedOnly", chartFilterMode === "closed" ? "true" : "false");

      // Pagination params
      params.set("page", String(tablePage));
      params.set("pageSize", String(tablePageSize));

      const response = await fetch(`/api/dashboard/summary?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        const { data } = result;
        setMetrics(data.metrics);
        setCashTrend(data.cashTrend || []);
        setCountryData(data.countryData || []);
        setLeadSourceData(data.leadSourceData || []);
        setOutcomeBreakdown(data.outcomeBreakdown || []);
        setFilterOptions({
          closers: data.filterOptions?.closers || [],
          setters: data.filterOptions?.setters || [],
          funnels: data.filterOptions?.funnels || [],
          contents: data.filterOptions?.contents || [],
          countries: data.filterOptions?.countries || [],
          sources: data.filterOptions?.sources || [],
          offers: data.filterOptions?.offers || [],
        });
        setRecentCalls(data.recentCalls || []);

        // Update pagination state from server response
        if (data.pagination) {
          setTableTotalItems(data.pagination.totalItems);
          setTableTotalPages(data.pagination.totalPages);
        }
      }
    } catch (error) {
      console.error("Failed to fetch acquisition data:", error);
    } finally {
      if (showLoading) {
        if (tableOnly) {
          setTableLoading(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [globalDateRange, formatForApi, dateField, closers, setters, sources, content, country, funnels, callOutcome, offer, offers, chartFilterMode, tablePage, tablePageSize, offerDashboard.dashboardSlug, offerDashboard.isEmbeddedMode]);

  // Reset to page 1 when filters change
  useEffect(() => {
    isPaginationChangeRef.current = false;
    setTablePage(1);
  }, [globalDateRange, dateField, closers, setters, sources, content, country, funnels, callOutcome, offer, offers, chartFilterMode]);

  // Fetch data when dependencies change
  useEffect(() => {
    const isTableOnly = isPaginationChangeRef.current;
    isPaginationChangeRef.current = false;
    fetchData(true, isTableOnly);
  }, [fetchData]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    // Skip if filterOptions haven't loaded yet
    if (!filterOptions.closers.length && !filterOptions.setters.length && !filterOptions.funnels.length) {
      return;
    }

    const filtersToRemove: string[] = [];

    // Check closer filter
    if (closers.length > 0 && filterOptions.closers.length > 0 && !closers.every(c => filterOptions.closers.includes(c))) {
      filtersToRemove.push('closer');
    }

    // Check setter filter
    if (setters.length > 0 && filterOptions.setters.length > 0 && !setters.every(s => filterOptions.setters.includes(s))) {
      filtersToRemove.push('setter');
    }

    // Check funnel filter (uses value/label format)
    if (funnels.length > 0 && filterOptions.funnels.length > 0) {
      const funnelValues = filterOptions.funnels.map(f => f.value);
      if (!funnels.every(f => funnelValues.includes(f))) {
        filtersToRemove.push('funnel');
      }
    }

    // Check content filter (uses value/label format)
    if (content && filterOptions.contents.length > 0) {
      const contentValues = filterOptions.contents.map(c => c.value);
      if (!contentValues.includes(content)) {
        filtersToRemove.push('content');
      }
    }

    // Check country filter
    if (country && filterOptions.countries.length > 0 && !filterOptions.countries.includes(country)) {
      filtersToRemove.push('country');
    }

    // Check source filter
    if (sources.length > 0 && filterOptions.sources.length > 0) {
      if (!sources.every(s => filterOptions.sources.includes(s))) {
        filtersToRemove.push('source');
      }
    }

    // Remove invalid filters
    if (filtersToRemove.includes('closer')) setClosers([]);
    if (filtersToRemove.includes('setter')) setSetters([]);
    if (filtersToRemove.includes('funnel')) setFunnels([]);
    if (filtersToRemove.includes('content')) setContents([]);
    if (filtersToRemove.includes('country')) setCountries([]);
    if (filtersToRemove.includes('source')) setSources([]);
  }, [filterOptions, closers, setters, funnels, content, country, sources, setClosers, setSetters, setFunnels, setContents, setCountries, setSources]);

  // Get greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };

  // Handle clear all filters
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Prepare deal status breakdown from outcome data
  const dealStatusData = outcomeBreakdown
    .filter((o) => o._id && o._id.trim() !== "")
    .slice(0, 6)
    .map((o) => ({
      name: o._id,
      value: o.count,
    }));

  // Map all API data into DashboardData format for widgets
  const dashboardData: DashboardData = useMemo(() => ({
    page: "acquisition",
    metrics: {
      totalCashCollected: metrics?.totalCashCollected || 0,
      totalRevenue: metrics?.totalRevenue || 0,
      closeRate: metrics?.closeRate || 0,
      showRate: metrics?.showRate || 0,
      callsDue: metrics?.callsDue || 0,
      callsTaken: metrics?.callsTaken || 0,
      callsClosed: metrics?.callsClosed || 0,
      avgCashPerCall: metrics?.avgCashPerCall || 0,
      avgCashPerClose: metrics?.avgCashPerClose || 0,
      newCashCollected: metrics?.newCashCollected || 0,
      mrrCash: metrics?.mrrCash || 0,
      totalGrossRevenue: metrics?.totalGrossRevenue || 0,
    },
    chartData: {
      countryData: countryData as unknown as Record<string, unknown>[],
      leadSourceData: leadSourceData as unknown as Record<string, unknown>[],
      outcomeBreakdown: dealStatusData as unknown as Record<string, unknown>[],
    },
    chartInteractions: {
      countryData: {
        onSelect: (name) => {
          if (name === "Other" || name === "Unknown" || name === "N/A") return;
          setCountry(country === name ? "" : name);
        },
        isActive: (name) => country === name,
      },
      leadSourceData: {
        onSelect: (name) => {
          if (name === "Other" || name === "Unknown" || name === "N/A") return;
          setSource(source === name ? "" : name);
        },
        isActive: (name) => source === name,
      },
      outcomeBreakdown: {
        onSelect: (name) => {
          if (!name.trim()) return;
          setCallOutcome(callOutcome === name ? "" : name);
        },
        isActive: (name) => callOutcome === name,
      },
    },
    tableData: {
      recentCalls: recentCalls as unknown as Record<string, unknown>[],
    },
    filterOptions: {
      closers: filterOptions.closers,
      setters: filterOptions.setters,
      funnels: filterOptions.funnels,
      contents: filterOptions.contents,
      countries: filterOptions.countries,
      sources: filterOptions.sources,
      offers: filterOptions.offers,
    },
    rawData: {
      pagination: {
        totalItems: tableTotalItems,
        totalPages: tableTotalPages,
        currentPage: tablePage,
        pageSize: tablePageSize,
      },
      tableLoading,
      handlePageChange,
      handlePageSizeChange,
      handleEditCall,
      handleDeleteCall,
    },
    isLoading: isLoading && !metrics,
    refetch: () => fetchData(false),
  }), [
    metrics,
    countryData,
    leadSourceData,
    dealStatusData,
    recentCalls,
    filterOptions,
    tableTotalItems,
    tableTotalPages,
    tablePage,
    tablePageSize,
    tableLoading,
    handlePageChange,
    handlePageSizeChange,
    handleEditCall,
    handleDeleteCall,
    isLoading,
    fetchData,
    country,
    source,
    callOutcome,
    setCountry,
    setSource,
    setCallOutcome,
  ]);

  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <StatCardSkeleton key={i} index={i + 2} />
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <AdditionalStatCardSkeleton key={i} index={i} />
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 + i * 0.05 }}
              className="relative overflow-hidden rounded-xl border border-border bg-card p-6"
            >
              <div className="mb-4 h-5 w-40 rounded bg-muted animate-pulse" />
              <div className="h-[300px] rounded-lg bg-muted/30 animate-pulse" />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  // Filter bar JSX
  const filterBar = (
    <FilterBar
      showDateFieldToggle={true}
      dateField={dateField as "timestamp" | "dateCallWasTaken"}
      onDateFieldChange={(v) => setDateField(v)}
      filters={[
        {
          key: "closer",
          label: "Select Closer",
          options: filterOptions.closers.map((c) => ({ value: c, label: c })),
          value: closers,
          onChange: (v) => setClosers(v as string[]),
          multiple: true,
        },
        {
          key: "setter",
          label: "Select Setter",
          options: filterOptions.setters.map((s) => ({ value: s, label: s })),
          value: setters,
          onChange: (v) => setSetters(v as string[]),
          multiple: true,
        },
        {
          key: "source",
          label: "Select Source",
          options: filterOptions.sources.map((s) => ({ value: s, label: s })),
          value: sources,
          onChange: (v) => setSources(v as string[]),
          multiple: true,
        },
        {
          key: "funnel",
          label: "Select Funnel",
          options: filterOptions.funnels,
          value: funnels,
          onChange: (v) => setFunnels(v as string[]),
          multiple: true,
        },
        ...(!offerDashboard.isEmbeddedMode && offer === "all" && filterOptions.offers.length > 0
          ? [{
              key: "offers",
              label: "Select Offers",
              options: filterOptions.offers.map((o: string) => ({ value: o, label: o })),
              value: offers.filter((o: string) => o !== "all"),
              onChange: (v: string | string[]) => setOffers(["all", ...(v as string[])]),
              multiple: true,
            }]
          : []),
      ]}
      onClearAll={handleClearAll}
    />
  );

  // Header JSX
  const header = (
    <motion.h1
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 }}
      className="text-2xl font-bold tracking-tight"
    >
      {getGreeting()}
    </motion.h1>
  );

  return (
    <DashboardDataProvider value={dashboardData}>
      <DashboardShell
        page="acquisition"
        defaultLayout={ACQUISITION_DEFAULT}
        filterBar={filterBar}
        header={header}
      >
        {/* Edit Dialog */}
        <EditRowDialog
          open={!!editingCall}
          onOpenChange={(open) => !open && setEditingCall(null)}
          title="Edit Call"
          description="Update call information"
          fields={CALL_EDIT_FIELDS}
          data={editingCall as Record<string, unknown> | null}
          onSave={handleSaveCall}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={!!deletingCall}
          onOpenChange={(open) => !open && setDeletingCall(null)}
          title="Delete Call"
          itemName={deletingCall?.leadName}
          onConfirm={confirmDeleteCall}
        />
      </DashboardShell>
    </DashboardDataProvider>
  );
}
